import {
    Avatar,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Stack,
    styled,
    TextField,
    Typography,
} from "@mui/material";
import TabsUnstyled from "@mui/base/TabsUnstyled";
import CloudUploadSvg from "jsx:../../../../Images/Icons/FontAwesome/solid/cloud-arrow-up.svg";
import RetrySvg from "jsx:../../../../Images/Icons/FontAwesome/solid/arrow-rotate-right.svg";
import {
    BUTTONS_BASE_STYLES,
    CTA_BUTTONS_STYLES,
    TEXT_ELLIPSIS,
} from "../../../Shared/styles";
import { StyledTextActionButton } from "../PostActions/PostActionButtons.styles";
import { MODAL_TITLE_STYLES } from "../../../../styles";

export const FormContainerStack = styled(Stack)({
    margin: "0 16px",
});

export const TabsStyled = styled(TabsUnstyled)({
    height: "100%",
});

export const PostFormHeaderContainer = styled(Stack)({
    justifyContent: "space-between",
    width: "100%",
    alignItems: "center",
    "& > button": {
        maxWidth: "max-content",
    },
});

export const FormHeader = styled(Typography)({
    ...MODAL_TITLE_STYLES,
});

export const PostFormGroupNameTypography = styled(Typography)({
    fontSize: "12px",
    color: "var(--community-color-secondary-text)",
    fontWeight: "400",
    ...TEXT_ELLIPSIS,
});

export const PostFormAvatar = styled(Avatar, {
    shouldForwardProp: (prop) => prop !== "backgroundColor",
})<{ backgroundColor?: string }>(({ backgroundColor }) => ({
    backgroundColor:
        backgroundColor ?? "var(--community-avatar-background-color)",
}));

export const PostFormHeaderTitleSection = styled(Box)({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    svg: {
        color: "var(--community-color-secondary-text)",
        width: "12px",
        height: "12px",
        marginRight: "4px",
    },
});

export const FormInputStack = styled(Stack)({
    cursor: "pointer",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    span: {
        fontSize: "14px",
        fontWeight: "500",
    },
    svg: {
        width: "24px",
        height: "24px",
    },
});

export const AddressContentContainer = styled("div")({
    display: "flex",
    flexDirection: "row",
    width: "100%",
    overflow: "hidden",
    alignItems: "center",
});

export const PostTextOutlinedInput = styled("textarea")({
    height: "48px",
    minHeight: "48px",
    resize: "none",
    border: "1px solid var(--community-color-transparent-black-05)",
    color: "var(--community-color-primary-text)",
    overflow: "hidden",
    fontSize: "14px",
    padding: "8px",
    borderRadius: "8px",
    "::placeholder": {
        fontSize: "14px",
        color: "var(--community-placeholder-color)",
        transparent: "50",
        fontWeight: "400",
    },
    ":focus": {
        outline: "none",
        border: "1px solid var(--community-color-dark-gray)",
    },
    "&._error": {
        borderColor: "var(--community-color-error-red)",
    },
});

export const FormImageInputStack = styled(Stack)({
    cursor: "pointer",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    height: "100%",
    input: {
        display: "none",
    },
    span: {
        fontSize: "14px",
        fontWeight: "500",
    },
    svg: {
        color: "var(--community-color-primary-text)",
        fontSize: "inherit",
        width: "24px",
        height: "24px",
    },
});

export const ImagePreviewStack = styled(Stack, {
    shouldForwardProp: (prop) => prop !== "scrollable" && prop !== "hideGap",
})<{ hideGap?: boolean; scrollable?: boolean }>(({ scrollable, hideGap }) => ({
    borderRadius: "4px",
    flexWrap: scrollable ? "nowrap" : "wrap",
    gap: hideGap ? "0" : "8px",
}));

export const CheckboxFormControlLabel = styled(FormControlLabel, {
    shouldForwardProp: (prop) => prop !== "isHidden",
})<{ isHidden?: boolean }>(({ isHidden }) => ({
    display: isHidden ? "none" : "flex",
    alignItems: "center",
    justifyContent: "start",
    marginRight: "0",
    marginLeft: "0",
    marginTop: "8px",
    cursor: "pointer",
    div: {
        marginRight: "8px",
        span: {
            width: "32px",
            height: "32px",
        },
    },
    span: {
        fontSize: "12px",
        "@media(min-width:640px)": {
            fontSize: "14px",
        },
    },
}));

export const InputsContainer = styled(Box)({
    marginRight: "0",
    marginLeft: "0",
    display: "flex",
    flexDirection: "column",
    marginTop: "16px",
    marginBottom: "16px",
    gap: "16px",
    borderRadius: "8px",
});

export const ImagesPreviewsContainer = styled(Box)({
    overflowX: "auto",
    marginRight: "0",
    marginLeft: "0",
    display: "flex",
    flexDirection: "column",
    marginTop: "12px",
});

export const InputsContainerTitleTypography = styled(Typography)({
    fontSize: "14px",
    fontWeight: 700,
    color: "var(--community-color-secondary-text)",
});

export const InputsContainerBoldTitleTypography = styled(
    InputsContainerTitleTypography,
)({
    fontWeight: "700",
});

export const ImagesFormControlLabel = styled(FormControlLabel)({
    cursor: "pointer",
    display: "inline-flex",
    margin: "0",
    span: {
        fontWeight: "700",
        fontSize: "16px",
    },
});

export const SubmitButton = styled(Button)({
    ...CTA_BUTTONS_STYLES,
    flex: "1",
    minWidth: "max-content",
    width: "max-width",
    backgroundColor: "var(--community-color-bright-azure)",
    "&:hover": {
        backgroundColor: "var(--community-color-bright-azure)",
    },
    textTransform: "none",
    color: "var(--community-color-snow-white)",
});

export const UploadMoreImagesButton = styled(StyledTextActionButton)({
    margin: 0,
    backgroundColor: "var(--community-color-snow-white)",
    "&:hover": {
        backgroundColor: "var(--community-color-snow-white)",
    },
    ...BUTTONS_BASE_STYLES,
});

export const SubmitStack = styled(Stack)({
    margin: "0 16px",
    paddingBottom: "16px",
    gap: "16px",
    flexDirection: "row",
    alignItems: "start",
    flexWrap: "wrap",
});

export const ModalActionsSubmitStack = styled(Stack)({
    gap: "16px",
    flexDirection: "row",
    alignItems: "start",
    flexWrap: "wrap",
});

export const LoadingStack = styled(Stack)({
    margin: "0 16px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
});
export const DeleteImageIconButton = styled(IconButton)({
    background: "#000",
    opacity: ".5",
    color: "var(--community-color-snow-white)",
    float: "right",
    padding: "4px",
    "&:hover": {
        background: "#000",
    },
    svg: {
        width: "16px",
        height: "16px",
        fill: "var(--community-color-snow-white)",
    },
    "z-index": 12,
    margin: "2px",
});

export const ImagePreviewContainer = styled("div")({
    height: "48px",
    width: "48px",
    "@media(min-width:640px)": {
        height: "120px",
        width: "120px",
    },
    borderRadius: "4px",
    position: "relative",
});

export const ImagePreviewItem = styled("div")<{ image?: string }>(
    ({ image }) => ({
        height: "48px",
        width: "48px",
        "@media(min-width:640px)": {
            height: "120px",
            width: "120px",
        },
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundColor: "var(--community-color-transparent-black-05)",
        backgroundImage: `url(${image})`,
        borderRadius: "4px",
        marginRight: "8px",
    }),
);

export const ImageOverlay = styled("div")({
    position: "absolute",
    top: "0",
    left: "0",
    height: "100%",
    width: "100%",
    backgroundColor: "rgba(0.8, 0.8, 0.8, 0.33)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "4px",
});

export const UploadIndicator = styled(CloudUploadSvg)({
    width: "24px",
    height: "24px",
    "@media(min-width:640px)": {
        width: "48px",
        height: "48px",
    },
    fill: "var(--community-color-snow-white)",
    animation: "flashing .5s linear infinite alternate",
    "@keyframes flashing": {
        "0%": {
            opacity: "1",
        },
        "100%": {
            opacity: "0.5",
        },
    },
});

export const RetryButton = styled(IconButton)({
    width: "24px",
    height: "24px",
    padding: "0",
    "@media(min-width:640px)": {
        width: "48px",
        height: "48px",
    },
});

export const RetryIndicator = styled(RetrySvg)({
    width: "48px",
    height: "48px",
    fill: "var(--community-color-snow-white)",
    cursor: "pointer",
});

export const FormDescriptionTypography = styled(Typography)({
    flex: "3",
    fontSize: "14px !important",
});

export const ErrorContainer = styled(Typography)({
    color: "var(--community-color-error-red)",
    fontSize: "0.75rem",
    lineHeight: "1.66",
    margin: "5px 14px 0px",
    textAlign: "left",
});

export const InfoContainer = styled(Stack)({
    backgroundColor: "var(--community-color-transparent-light-steal-blue-10)",
    color: "var(--community-color-light-steal-blue)",
    fontSize: "12px",
    borderRadius: "4px",
    padding: "8px",
    lineHeight: "1.5",
});

export const InfoTypography = styled(Typography)({
    fontSize: "12px",
    lineHeight: "1.5",
});

export const BoldText = styled("span")({
    fontWeight: "700",
});

export const LinkoutUrlTextField = styled(TextField)({
    ".MuiOutlinedInput-notchedOutline": {
        border: "1px solid var(--community-color-transparent-black-05) !important",
    },
    ".Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: "1px solid var(--community-color-dark-gray) !important",
    },
    "& input::placeholder": {
        fontSize: "14px",
        color: "var(--community-placeholder-color)",
        fontWeight: "400",
    },
    "& input": {
        fontSize: "14px",
        color: "var(--community-color-primary-text)",
        fontWeight: "400",
        backgroundColor: "var(--community-color-snow-white)",
        borderRadius: "8px",
        flex: "1",
        ...TEXT_ELLIPSIS,
        padding: "8px",
    },
    "&._error": {
        borderColor: "var(--community-color-error-red)",
    },
    "& > div": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginTop: "8px",
        borderRadius: "8px",
        paddingRight: "16px",
        paddingLeft: "8px",
    },
    "& .MuiFormHelperText-root.Mui-error": {
        position: "absolute",
    },
    fieldset: {
        top: "0px",
        legend: { height: "0px" },
    },
});

export const PostLinkoutUrlTextField = styled(TextField)({
    ".MuiOutlinedInput-notchedOutline": {
        border: "1px solid var(--community-color-transparent-black-05) !important",
    },
    ".Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: "1px solid var(--community-color-dark-gray) !important",
    },
    "& input::placeholder": {
        fontSize: "14px",
        color: "var(--community-placeholder-color)",
        fontWeight: "400",
    },
    "& input": {
        height: "40px",
        fontSize: "14px",
        color: "var(--community-color-primary-text)",
        fontWeight: "400",
        backgroundColor: "var(--community-color-snow-white)",
        borderRadius: "8px",
        flex: "1",
        ...TEXT_ELLIPSIS,
        padding: "8px",
    },
    "&._error": {
        borderColor: "var(--community-color-error-red)",
    },
    "& > div": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "8px",
        paddingRight: "8px",
        paddingLeft: "8px",
        height: "40px",
        "@media(min-width:640px)": {
            height: "48px",
        },
    },
    "& .MuiFormHelperText-root.Mui-error": {
        position: "absolute",
    },
    fieldset: {
        top: "0px",
        legend: { height: "0px" },
    },
});

export const PostPollOptionField = styled(PostLinkoutUrlTextField)({
    "& input::placeholder": {
        fontSize: "14px",
    },
    "& input": {
        fontSize: "14px",
        color: "var(--community-color-primary-text)",
        padding: "0 8px",
    },
    backgroundColor: "var(--community-color-snow-white)",
});

export const ClearButtonHolder = styled(InputAdornment)({
    backgroundColor: "white",
    height: "auto",
    maxHeight: "fit-content",
    margin: "0px",
    borderRadius: "0px 5px 5px 0px",
});

export const StyledIconButton = styled(IconButton)({
    width: "36px",
    height: "36px",
});

export const IsQuestionCheckbox = styled(Checkbox)({
    "&.Mui-checked > svg": {
        color: "var(--community-color-light-steal-blue)",
    },
});

export const DescriptionTypography = styled(Typography)({
    fontSize: "14px",
    color: "var(--community-color-primary-text)",
    ...TEXT_ELLIPSIS,
});

export const DescriptionSubTypography = styled(DescriptionTypography)({
    fontSize: "12px",
    color: "var(--community-color-secondary-text)",
});

export const PollSectionTitleTypography = styled(DescriptionTypography)({
    fontWeight: "700",
    color: "var(--community-color-secondary-text)",
});

const PREVIEW_BLOCK_STYLES = {
    borderRadius: "4px",
    padding: "4px 8px 4px 0",
    display: "flex",
    alignItems: "center",
    backgroundColor: "var(--community-color-transparent-light-steal-blue-10)",
    ":hover": {
        backgroundColor:
            "var(--community-color-transparent-light-steal-blue-20)",
    },
};

export const PollOptionsPreviewContainer = styled(Stack)({
    ...PREVIEW_BLOCK_STYLES,
    marginTop: "12px",
});

export const PreviewBlockImageContainer = styled(Box)({
    display: "flex",
    alignItems: "center",
    margin: "0 8px",
    "& svg": {
        color: "var(--community-color-light-steal-blue)",
    },
});

export const PollOptionsRatePreviewStack = styled(Stack)({
    alignItems: "center",
    flex: 1,
    flexDirection: "row",
});

export const PollOptionsPreviewContainerStack = styled(Stack)({
    flexDirection: "row",
    cursor: "pointer",
    width: "100%",
});

export const PollOptionsPreviewStack = styled(Stack)({
    flex: "1",
    width: "100%",
    overflow: "hidden",
});

export const OptionsContainer = styled(Stack)({
    flexDirection: "row",
    gap: "8px",
    flexWrap: "wrap",
    touchAction: "pan-y",
});

export const OptionsIconButton = styled(IconButton)({
    display: "flex",
    cursor: "pointer",
    flexDirection: "row",
    alignItems: "center",
    gap: "8px",
    width: "max-content",
    height: "32px",
    borderRadius: "90px",
    padding: "0 12px",
    backgroundColor: "var(--community-color-transparent-black-05)",
    ":hover": {
        backgroundColor: "var(--community-color-transparent-black-05)",
    },
    "& > svg": {
        width: "16px",
        height: "16px",
    },
    "& > p": {
        fontSize: "14px",
        fontWeight: "500",
        color: "var(--community-color-primary-text)",
    },
});

export const ImagePlaceholderContainer = styled(Box)({
    height: "48px",
    width: "48px",
    minWidth: "48px",
    border: "1px dashed var(--community-color-dark-gray)",
    "@media(min-width:640px)": {
        height: "120px",
        minWidth: "120px",
        width: "120px",
    },
    borderRadius: "4px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
});

export const SelectedContentButton = styled("div")({
    ...PREVIEW_BLOCK_STYLES,
    minHeight: "40px",
    fontSize: "14px",
    color: "var(--community-color-primary-text)",
    justifyContent: "flex-start",
    textTransform: "unset",
    lineHeight: "20px",
    cursor: "pointer",
});

export const CloseIconButton = styled(IconButton)({
    width: "32px",
    height: "32px",
    svg: {
        height: "24px",
        width: "24px",
        color: "var(--community-color-transparent-black-20)",
    },
});

export const SaveLinkoutButton = styled(Button)({
    width: "24px",
    height: "24px",
    minWidth: "24px",
    borderRadius: "90px",
    background:
        "linear-gradient(to right bottom, #50739d, #6272a3, #786fa6, #8f6ba3, #a4669c)",
    "&.Mui-disabled": {
        background: "var(--community-color-transparent-black-05)",
    },
    svg: {
        width: "16px",
        height: "16px",
        cursor: "pointer",
        fill: "var(--community-color-snow-white)",
    },
});

export const ClearLinkoutButton = styled(Button)({
    width: "24px",
    height: "24px",
    minWidth: "24px",
    borderRadius: "90px",
    svg: {
        width: "24px",
        height: "24px",
        color: "var(--community-color-transparent-black-20)",
    },
});

export const LinkoutEndAdornmentStack = styled(Stack)({
    flexDirection: "row",
    alignItems: "center",
    gap: "8px",
});
